import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { api, pesanKesalahan } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Button, Input } from '@/components'

/**
 * Halaman wajib ganti kata sandi — diletakkan DI LUAR layout terautentikasi
 * supaya tetap terjangkau saat backend memblokir seluruh endpoint lain
 * (lihat `wajibGantiKataSandi` di apps/api/src/security/auth.ts).
 */
export default function GantiPassword() {
  const { pengguna, keluar, segarkanProfil, siap } = useAuth()
  const navigate = useNavigate()
  const [lama, setLama] = useState('')
  const [baru, setBaru] = useState('')
  const [ulangi, setUlangi] = useState('')
  const [galat, setGalat] = useState<string | null>(null)
  const [pesan, setPesan] = useState<string | null>(null)
  const [memuat, setMemuat] = useState(false)

  if (siap && !pengguna) {
    return <Navigate to="/login" replace />
  }

  const kirim = async (e: FormEvent) => {
    e.preventDefault()
    setGalat(null)
    setPesan(null)
    if (!lama || !baru) {
      setGalat('Kata sandi lama dan baru wajib diisi.')
      return
    }
    if (baru.length < 10) {
      setGalat('Kata sandi baru minimal 10 karakter.')
      return
    }
    if (baru !== ulangi) {
      setGalat('Konfirmasi kata sandi tidak sama.')
      return
    }
    if (baru === lama) {
      setGalat('Kata sandi baru tidak boleh sama dengan kata sandi lama.')
      return
    }
    setMemuat(true)
    try {
      const hasil = await api.post<{ pesan?: string }>('/auth/change-password', {
        kataSandiLama: lama,
        kataSandiBaru: baru,
      })
      setPesan(
        hasil?.pesan ??
          'Kata sandi berhasil diubah. Seluruh sesi lain telah dicabut. Silakan masuk kembali.',
      )
      // Backend mencabut SEMUA sesi (termasuk sesi ini) setelah perubahan.
      window.setTimeout(() => void keluar(), 1500)
    } catch (err) {
      setGalat(pesanKesalahan(err))
    } finally {
      setMemuat(false)
    }
  }

  const lanjutTanpaGanti = async () => {
    const profil = await segarkanProfil()
    if (profil && !profil.mustChangePassword) navigate('/', { replace: true })
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-netral-100 p-4">
      <div className="w-full max-w-md">
        <form onSubmit={kirim} className="space-y-4 rounded-2xl border border-netral-200 bg-white p-6 shadow-sm">
          <div>
            <h1 className="text-lg font-semibold text-netral-900">Ganti Kata Sandi</h1>
            <p className="mt-1 text-sm text-netral-500">
              {pengguna?.mustChangePassword
                ? 'Anda wajib mengganti kata sandi sebelum dapat melanjutkan.'
                : 'Perbarui kata sandi akun Anda.'}
            </p>
          </div>

          {galat ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {galat}
            </div>
          ) : null}
          {pesan ? (
            <div role="status" className="rounded-lg border border-yayasan-300 bg-yayasan-50 px-3 py-2 text-sm text-yayasan-900">
              {pesan}
            </div>
          ) : null}

          <Input
            label="Kata sandi lama"
            type="password"
            autoComplete="current-password"
            value={lama}
            onChange={(e) => setLama(e.target.value)}
            required
          />
          <Input
            label="Kata sandi baru"
            type="password"
            autoComplete="new-password"
            value={baru}
            onChange={(e) => setBaru(e.target.value)}
            bantuan="Minimal 10 karakter, tidak boleh sama dengan kata sandi lama."
            required
          />
          <Input
            label="Ulangi kata sandi baru"
            type="password"
            autoComplete="new-password"
            value={ulangi}
            onChange={(e) => setUlangi(e.target.value)}
            galat={ulangi && ulangi !== baru ? 'Belum sama dengan kata sandi baru.' : null}
            required
          />

          <div className="flex flex-wrap gap-2">
            <Button type="submit" memuat={memuat}>
              Simpan Kata Sandi
            </Button>
            <Button varian="garis" onClick={() => void lanjutTanpaGanti()}>
              Periksa Kembali Status
            </Button>
            <Button varian="halus" onClick={() => void keluar()}>
              Keluar
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
